import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/sfacoctg.jpeg';
import { useLoginModal } from '../components/LoginModalContext';
import { useAuth } from '../context/AuthContext';
import PublicUserMenu from '../components/PublicUserMenu';
import {
  getContactContent,
  getDefaultContactContent,
  getDefaultSiteContent,
  getSiteContent
} from '../services/authService';
import './Home.css';
import './Contact.css';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactContent, setContactContent] = useState(getDefaultContactContent());
  const [siteContent, setSiteContent] = useState(getDefaultSiteContent());
  const { openLogin, openRegister } = useLoginModal();
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    getContactContent()
      .then((data) => setContactContent(data))
      .catch(() => {});
    getSiteContent()
      .then((data) => setSiteContent(data))
      .catch(() => {});
  }, []);

  const submit = (event) => {
    event.preventDefault();
    setSent(true);
  };

  const officeName = siteContent.companyName || contactContent.officeTitle;
  const officeAddress = siteContent.topHeaderAddress || contactContent.officeAddress;
  const officeEmail = siteContent.topHeaderEmail || contactContent.officeEmail;
  const officePhone = siteContent.topHeaderPhone || contactContent.officePhone;
  const mapEmbedUrl = officeAddress
    ? `https://maps.google.com/maps?q=${encodeURIComponent(officeAddress)}&t=&z=12&ie=UTF8&iwloc=&output=embed`
    : contactContent.mapEmbedUrl;

  return (
    <div className="contact-page">
      <div className="dashboard-topbar">
        <div className="dashboard-topbar-left">
          <span>{siteContent.companyName}</span>
          <span className="dot" />
          <span>{siteContent.topHeaderAddress}</span>
          <span className="dot" />
          <span>{siteContent.topHeaderEmail}</span>
          <span className="dot" />
          <span>{siteContent.topHeaderPhone}</span>
        </div>
        <div className="dashboard-topbar-right">
          <a href={siteContent.linkedinUrl} target="_blank" rel="noreferrer">LinkedIn</a>
          <a href={siteContent.instagramUrl} target="_blank" rel="noreferrer">Instagram</a>
          <a href={siteContent.facebookUrl} target="_blank" rel="noreferrer">Facebook</a>
          <a href={siteContent.whatsappUrl} target="_blank" rel="noreferrer">WhatsApp</a>
        </div>
      </div>

      <header className="dashboard-header">
        <div className="brand">
          <img className="brand-logo" src={siteContent.logoUrl || logo} alt="Safco CTG logo" />
          <div>
            <p className="brand-title">{siteContent.brandTitle}</p>
            <p className="brand-subtitle">{siteContent.brandSubtitle}</p>
          </div>
        </div>
        <nav className={`dashboard-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <Link to="/">Home</Link>
          <Link to="/members">Members</Link>
          <Link to="/stories">Stories</Link>
          <Link to="/about">About</Link>
          <Link to="/contact">Contact</Link>
          <div className="mobile-actions">
            {!isAuthenticated && (
              <>
                <button className="ghost-button" type="button" onClick={openLogin}>Login</button>
                <button className="ghost-button" type="button" onClick={openRegister}>Register</button>
              </>
            )}
            {isAuthenticated && (
              <>
                <Link className="ghost-button" to={user?.role === 'admin' ? '/dashboard/admin' : '/dashboard/member'}>Dashboard</Link>
                <button className="ghost-button" type="button" onClick={logout}>Logout</button>
              </>
            )}
            <Link className="ghost-button" to="/submit-payment">Donate</Link>
            <Link className="solid-button" to="/submit-payment">Submit Payment</Link>
          </div>
        </nav>
        <div className="dashboard-actions">
          {!isAuthenticated && (
            <>
              <button className="ghost-button" type="button" onClick={openLogin}>Login</button>
              <button className="ghost-button" type="button" onClick={openRegister}>Register</button>
            </>
          )}
          {isAuthenticated && (
            <PublicUserMenu />
          )}
          <Link className="ghost-button" to="/submit-payment">Donate</Link>
          <Link className="solid-button" to="/submit-payment">Submit Payment</Link>
        </div>
        <button
          className="menu-toggle"
          type="button"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <section className="contact-hero">
        <div className="contact-hero-content">
          <p className="contact-eyebrow">{contactContent.heroEyebrow}</p>
          <h1>{contactContent.heroTitle}</h1>
          <p className="contact-lead">{contactContent.heroLead}</p>
          <div className="contact-actions">
            <Link className="ghost-button" to="/">Back to home</Link>
            <a className="solid-button" href="#contact-form">Send a message</a>
          </div>
        </div>
        <div className="contact-hero-card">
          <h3>{officeName}</h3>
          <p>{officeAddress}</p>
          <p>{officeEmail}</p>
          <p>{officePhone}</p>
          <div className="contact-pill">{contactContent.officeBadge}</div>
        </div>
      </section>

      <section className="contact-grid">
        <div className="contact-map">
          <iframe
            title={`${officeName} map`}
            src={mapEmbedUrl}
            loading="lazy"
          />
        </div>
        <form id="contact-form" className="contact-form" onSubmit={submit}>
          <h2>{contactContent.formTitle}</h2>
          <p>{contactContent.formText}</p>
          <label>
            Full name
            <input
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label>
            Email address
            <input
              type="email"
              placeholder="you@alumni.org"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </label>
          <label>
            Message
            <textarea
              rows="4"
              placeholder="How can we support you?"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
            />
          </label>
          {sent && <p className="contact-sent">Message queued. We’ll be in touch soon.</p>}
          <button type="submit" className="solid-button">Send message</button>
        </form>
      </section>

      <section className="contact-highlights">
        {contactContent.highlights.map((highlight) => (
          <div key={highlight.title}>
            <h3>{highlight.title}</h3>
            <p>{highlight.text}</p>
          </div>
        ))}
      </section>

      <footer className="dashboard-footer">
        <div>
          <img className="footer-logo" src={logo} alt="Safco CTG logo" />
          <h3>SFACO-CTG</h3>
          <p>
            Building lifelong connections between graduates, students, and faculty.
          </p>
        </div>
        <div className="footer-links">
          <div>
            <h4>Community</h4>
            <Link to="/contact">Contact</Link>
            <a href="/#events">Events</a>
            <a href="/#chapters">Chapters</a>
          </div>
          <div>
            <h4>Support</h4>
            <a href="/#giving">Giving</a>
            <a href="mailto:alumni@chatar.org">Email us</a>
            <a href="tel:+16175550142">Call office</a>
          </div>
          <div>
            <h4>Resources</h4>
            <Link to="/stories">Newsroom</Link>
            <Link to="/register">Register</Link>
            <Link to="/">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
